/**
 * /tm-invoices/$invoiceId — T&M Invoice detail.
 *
 * Shows:
 *  - Header: invoice # / period / project / status / totals
 *  - Line breakdown table (labor / materials / equipment)
 *  - Included tickets list
 *  - Actions: Issue / Void / Download PDF
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Chip, Divider, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";

import { api } from "@/lib/api";
import { tmInvoiceDetailQueryOptions, tmInvoiceKeys } from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type { TMInvoice, TMInvoiceStatus } from "@/lib/types";

export const Route = createFileRoute("/_auth/tm-invoices/$invoiceId")({
  component: TMInvoiceDetailPage,
});

const STATUS_CHIP: Record<TMInvoiceStatus, { label: string; color: "default" | "warning" | "success" | "error" | "info" }> = {
  draft:  { label: "Draft",  color: "default" },
  sent:   { label: "Sent",   color: "warning" },
  paid:   { label: "Paid",   color: "success" },
  voided: { label: "Voided", color: "error"   },
};

const LINE_TYPE_CHIP: Record<string, { label: string; color: "default" | "primary" | "secondary" | "info" | "success" }> = {
  labor:     { label: "Labor",     color: "primary"   },
  material:  { label: "Material",  color: "secondary" },
  equipment: { label: "Equipment", color: "info"      },
};

function money(v: string | number | null | undefined): string {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}

function TMInvoiceDetailPage() {
  const { invoiceId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const invQ = useQuery(tmInvoiceDetailQueryOptions(invoiceId));
  const inv = invQ.data;

  const issue = useMutation({
    mutationFn: () =>
      api<{ envelope_id: string; auto_approved: boolean }>(`/api/money/tm-invoices/${invoiceId}/issue`, {
        method: "POST",
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: tmInvoiceKeys.detail(invoiceId) });
      qc.invalidateQueries({ queryKey: tmInvoiceKeys.all });
      const msg = res.auto_approved
        ? "Invoice issued and auto-approved — GL entries posted."
        : "Invoice issued — pending approval.";
      toast.success(msg);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const voidInv = useMutation({
    mutationFn: () =>
      api(`/api/money/tm-invoices/${invoiceId}/void`, { method: "POST", body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tmInvoiceKeys.detail(invoiceId) });
      qc.invalidateQueries({ queryKey: tmInvoiceKeys.all });
      toast.success("Invoice voided.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (invQ.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (!inv) return <div className="p-6 text-slate-500">Invoice not found.</div>;

  const chip = STATUS_CHIP[inv.status] ?? { label: inv.status, color: "default" };
  const canIssue  = inv.status === "draft";
  const canVoid   = inv.status === "draft" || inv.status === "sent";
  const invLabel  = inv.invoice_number || inv.id.slice(0, 8).toUpperCase();

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate({ to: "/tm-invoices" })}
          >
            All Invoices
          </Button>
          <Typography variant="h5">{invLabel}</Typography>
          <Chip label={chip.label} color={chip.color} size="small" />
        </div>

        <div className="flex gap-2">
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            component="a"
            href={`/api/money/tm-invoices/${invoiceId}/pdf`}
            target="_blank"
          >
            Download PDF
          </Button>
          {canIssue && (
            <Button
              variant="contained"
              size="small"
              disabled={issue.isPending}
              onClick={() => {
                if (confirm("Issue this invoice? Eligible tickets will be locked."))
                  issue.mutate();
              }}
            >
              {issue.isPending ? "Issuing…" : "Issue"}
            </Button>
          )}
          {canVoid && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={voidInv.isPending}
              onClick={() => {
                if (confirm("Void this invoice?")) voidInv.mutate();
              }}
            >
              Void
            </Button>
          )}
        </div>
      </div>

      {/* Info card */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Field label="Period"       value={`${inv.period_start} – ${inv.period_end}`} />
          <Field label="Rate Sheet"   value={inv.rate_sheet_id} />
          <Field label="Project"      value={inv.project_id} />
          <Field label="Notes"        value={inv.notes} />
        </div>

        <Divider sx={{ my: 2 }} />

        <div className="grid grid-cols-4 gap-4 text-center">
          <KpiTile label="Labor"     value={money(inv.labor_total)} />
          <KpiTile label="Materials" value={money(inv.materials_total)} />
          <KpiTile label="Equipment" value={money(inv.equipment_total)} />
          <KpiTile label="Total"     value={money(inv.grand_total)} bold />
        </div>
      </div>

      {/* Line items */}
      <SectionCard title="Line Items">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Work Date</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Billable</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inv.lines.map((line) => {
              const lc = LINE_TYPE_CHIP[line.line_type] ?? { label: line.line_type, color: "default" };
              return (
                <TableRow key={line.id}>
                  <TableCell>
                    <Chip label={lc.label} color={lc.color} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{line.description}</TableCell>
                  <TableCell>{line.work_date ?? "—"}</TableCell>
                  <TableCell align="right">{line.quantity ? Number(line.quantity).toFixed(2) : "—"}</TableCell>
                  <TableCell>{line.unit ?? "—"}</TableCell>
                  <TableCell align="right">{money(line.rate)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{money(line.billable)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </SectionCard>

      {/* Included tickets */}
      <SectionCard title={`Included Tickets (${inv.invoice_tickets.length})`}>
        <div className="px-2 py-1 flex flex-wrap gap-2">
          {inv.invoice_tickets.map((it) => (
            <Chip
              key={it.ticket_id}
              label={it.ticket_id.slice(0, 8).toUpperCase()}
              size="small"
              variant="outlined"
              onClick={() => navigate({ to: "/tm-tickets/$ticketId", params: { ticketId: it.ticket_id } })}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <span className="text-slate-500">{label}: </span>
      <span className="font-medium">{value ?? "—"}</span>
    </div>
  );
}

function KpiTile({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="bg-slate-50 rounded p-3 dark:bg-gray-800">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-lg ${bold ? "font-bold text-slate-900 dark:text-white" : "font-semibold text-slate-700 dark:text-slate-200"}`}>
        {value}
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 dark:bg-gray-900 dark:border-gray-700">
      <div className="px-5 py-3 border-b border-slate-100 dark:border-gray-700">
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}
